import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { FacetSourceOption as FacetOption } from './filterAdapter';
import type { AttachKind } from './types';

export interface AttachFacets {
  typeFacet: FacetOption[];
  ownerFacet: FacetOption[];
}

const WORK_ITEM_TYPE_FACET: FacetOption[] = [
  { value: 'story', label: 'Story' },
  { value: 'bug', label: 'Bug' },
  { value: 'task', label: 'Task' },
];

/**
 * Resolve the type + owner facet option lists for the attach-existing toolbar.
 *
 * Owner facet (all kinds): the org membership list via `list_org_members`.
 * Type facet: live `document_types` for documents, the static story/bug/task
 * set for work-items, and empty for epics (no Type column).
 *
 * Keyed on `[kind, orgId, enabled]` with a `cancelled` guard so a fast
 * org-switch or modal-close doesn't write stale results.
 */
export function useAttachFacets(
  kind: AttachKind,
  orgId: string | null,
  enabled: boolean
): AttachFacets {
  const [typeFacet, setTypeFacet] = useState<FacetOption[]>([]);
  const [ownerFacet, setOwnerFacet] = useState<FacetOption[]>([]);

  useEffect(() => {
    if (!orgId || !enabled) {
      setTypeFacet([]);
      setOwnerFacet([]);
      return;
    }
    let cancelled = false;

    (async () => {
      const { data: members } = await supabase.rpc('list_org_members', {
        p_org_id: orgId,
      });
      if (cancelled) return;
      const owners: FacetOption[] = (
        (members ?? []) as { user_id?: string | null; display_name?: string | null }[]
      )
        .filter((m): m is { user_id: string; display_name?: string | null } =>
          Boolean(m.user_id)
        )
        .map((m) => ({ value: m.user_id, label: m.display_name ?? '' }));
      setOwnerFacet(owners);

      if (kind === 'documents') {
        const { data: types } = await supabase
          .from('document_types')
          .select('id, name')
          .eq('org_id', orgId)
          .is('archived_at', null)
          .order('name');
        if (cancelled) return;
        setTypeFacet(
          ((types ?? []) as { id: string; name: string }[]).map((t) => ({
            value: t.id,
            label: t.name,
          }))
        );
      } else if (kind === 'work-items') {
        setTypeFacet(WORK_ITEM_TYPE_FACET);
      } else {
        setTypeFacet([]);
      }
    })().catch(() => {
      if (cancelled) return;
      setTypeFacet(kind === 'work-items' ? WORK_ITEM_TYPE_FACET : []);
      setOwnerFacet([]);
    });

    return () => {
      cancelled = true;
    };
  }, [kind, orgId, enabled]);

  return { typeFacet, ownerFacet };
}
